// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { AsyncPipe, CommonModule, DatePipe, NgStyle } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, Component, Input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, combineLatest, map } from 'rxjs';
import { ApiAlarmDetails } from 'src/app/api/remote-api/.tube.generated/models/api-alarm-details';
import { ApiMarker } from 'src/app/api/remote-api/.tube.generated/models/api-marker';
import { ApiTube } from 'src/app/api/remote-api/.tube.generated/models/api-tube';
import { LocalizedDatePipe } from 'src/app/modules/shared/pipes/localizedDate.pipe';
import { SettingsService } from 'src/app/modules/shared/services/settings.service';
import { DeepReadonly } from 'src/app/modules/shared/utils/api-call-state.util';
import { Observe } from 'src/app/modules/shared/utils/as-observable';
import { StringUtils } from 'src/app/modules/shared/utils/stringUtils';

@Component({
  selector: 'app-tube-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    NgStyle,
    AsyncPipe,
    DatePipe,
    TranslateModule,
    LocalizedDatePipe,
    CommonModule,
  ],
})
export class TimelineComponent {
  @Input({ required: true }) data!: DeepReadonly<ApiTube>;
  @Observe('data') private data$!: Observable<DeepReadonly<ApiTube>>;

  @Input({ required: true }) markers!: DeepReadonly<ApiMarker[]>;
  @Observe('markers') private markers$!: Observable<DeepReadonly<ApiMarker[]>>;

  // compute additional data needed for rendering
  isDGA$ = this.data$.pipe(
    map((data) => data.dataSource.toLocaleUpperCase() === 'DGA')
  );
  tubeHistories$ = combineLatest([
    this.data$,
    this.markers$,
    this.settingsController.timelineAscending$,
  ]).pipe(
    map(([{ tubeHistory }, markers, timelineAscending]) =>
      tubeHistory
        .map((history) => {
          const stateAlarm = TimelineComponent.filterAlarmDetail(
            history.alarmDetails,
            'State'
          );
          const arcingsAlarms = TimelineComponent.filterAlarmDetail(
            history.alarmDetails,
            'Arcings'
          );
          const xraErrorAlarms = TimelineComponent.filterAlarmDetail(
            history.alarmDetails,
            'XRAError'
          );

          const marker = markers.find(
            (m) => m.generalSeverity === stateAlarm?.hitCounterOrMarkerId
          );

          return {
            ...history,
            stateIcon: marker?.icon,
            stateIconColor: marker?.stateIconColor,
            recommendation: marker?.recommendation,
            arcingsAlarms,
            xraErrorAlarms,
            // only used for sorting below
            timestamp: new Date(history.logDay).getTime(),
          };
        })
        .sort((a, b) => {
          return (timelineAscending ? -1 : 1) * (b.timestamp - a.timestamp);
        })
    )
  );
  firstDate$ = this.tubeHistories$.pipe(map((h) => h[0]));
  lastDate$ = this.tubeHistories$.pipe(map((h) => h[h.length - 1]));

  constructor(public settingsController: SettingsService) {}

  private static filterAlarmDetail(
    alarmDetails: DeepReadonly<ApiAlarmDetails[]>,
    kindOfAlarm: string
  ): DeepReadonly<ApiAlarmDetails> | undefined {
    if (!alarmDetails || StringUtils.isNullOrEmpty(kindOfAlarm)) {
      return undefined;
    }
    const filter = kindOfAlarm.toLowerCase();

    const filtersResult = alarmDetails.filter(
      (item) => item.kindOfAlarm.toLowerCase() === filter
    );

    if (filtersResult.length > 0) {
      return filtersResult[0];
    }

    return {
      kindOfAlarm: kindOfAlarm,
      events: [],
      hitCounterOrMarkerId: 0,
    };
  }
}
